import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Form } from "antd";

import { generateInputID, getCoverageAndActivityKeyByID } from "../../../../AP/Steps/StepThree/components/Quotation/utils";
import { TheftInsuranceQuotationFormByTab } from "../components";

import QuotationService, { DEFAULT_PAYMENT_PLAN_CODE } from "../../../../../../../../shared/services/quotation";

import { GenericDataFormContext, LOADING_SAVED_QUOTATIONS_STATES, QUOTATION_BUTTON, QuotationButton, QuotationInfoContext, QuotationStep, StepOneFormContext } from "../../../../shared";
import { StepThreeFormContext } from "./StepThreeFormContext";
import { StepTwoFormContext } from "../../StepTwo/contexts";


import { GetCommertialsConditionsResponse, QuotationCoverage } from "../../../../../../../../shared/interfaces";
import { TheftInsuranceQuotationComponent, TheftInsuranceQuotationRequest, WebTheftInsuranceQuotation } from "../../../../../../../../shared/interfaces/services/theft-insurance-quotation-service";

import { WizardDataFormContext } from "../../../Wizard/contexts/WizardDataForm";
import { getValueDefaultPaymentMethod } from "../../../../utils/get-value-default-payment-method";
import { LOADING_RENEWAL_QUOTATION_STATES } from "../../../../shared/interfaces/loading-renewal-quotation-states-enum";
import { LoadQuotationToRenewResponse } from "../../../../shared/interfaces/load-renewal-quotation-response-interface";
import { scrollToEmitButton } from "../../../../../../../../shared/utils";

export interface TheftInsuranceQuotationForm {
  [key: string]: string;
};

export interface TheftInsurancePaymentMethodForm {
  [key: string]:          string | number;
  paymentMethod:          string;
  comission:              number;
  extraVariable:          number;
  variation:              number;
};

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    allLocalitiesPerProvince,
    paymentMethodsList,
    allCoveragesPerPlan,
    setPlansSelectedByTabs,
    setCanShowSpinner,
    updateFormsAfterRemoveTabs,
    setTabsUserCanNotDelete,
    removedTabIndexes,
    setRemovedTabIndexes,
    formValues,
  } = useContext(GenericDataFormContext);
  const {
    currentStep,
    quotationInfo,
    savedQuotation,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    setCurrentStep,
    renewalQuotationState,
    setRenewalQuotationState,
    quotationToRenewal
  } = useContext(QuotationInfoContext);
  const { clientSelected } = useContext( StepOneFormContext );
  const { handleTabErrors } = useContext(WizardDataFormContext);
  const { 
    tabs, 
    stepTwoForm,
    setIsPlanTypeADisabled,
    setIsPlanTypeCDisabled,
  } = useContext(StepTwoFormContext);

  const [ commertialConditions, setCommertialConditions ] = useState<GetCommertialsConditionsResponse>();
  const [ theftInsuranceQuotationComponents, setTheftInsuranceQuotationComponents ] = 
    useState<Array<TheftInsuranceQuotationComponent>>([]);
  const [ quotationResponse, setQuotationResponse ] = useState<WebTheftInsuranceQuotation | undefined>();
  const [ labelButton, setLabelButton ] = useState<QuotationButton>( quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR );
  
  const [ stepThreeTheftInsuranceQuotationForm ] = Form.useForm<TheftInsuranceQuotationForm>();
  const [ theftInsurancePaymentMethodsForm ] = Form.useForm<TheftInsurancePaymentMethodForm>();

  const [ quotationTabs, setQuotationTabs ] = useState<Array<any>>([]);
  const [ activeTab, setActiveTab ] = useState(1);

  const clearPrimaFieldsOnFormChange = useCallback(() => {
    const allFields = stepThreeTheftInsuranceQuotationForm.getFieldsValue()
    const allPrimaKeys = Object.keys( allFields ).filter( el => el.includes('prima') );
    stepThreeTheftInsuranceQuotationForm.resetFields( allPrimaKeys );
  }, [ stepThreeTheftInsuranceQuotationForm ]);

  const getCommertialsConditions = useCallback(async () => {
    try {
      const response = await QuotationService.getComertialsConditions( quotationInfo?.branch! );

      if(response.data) {
        setCommertialConditions( response.data );
      }
    } catch (error) {}
  }, [quotationInfo, setCommertialConditions]);

  const generateCoveragesInfo = useCallback(( tabKey: string ): Array<QuotationCoverage> => {
    const quotationCoverages: Array<QuotationCoverage> = [];

    const allFields = stepThreeTheftInsuranceQuotationForm.getFieldsValue();

    const allCheckboxFields = Object.keys(allFields).filter((el) => el.includes('checkbox'));

    const allCheckboxFieldByTab = allCheckboxFields.filter( el => el.includes(`theft-insurance-tab-${tabKey}`));

    allCheckboxFieldByTab.forEach((el) => {
      if(stepThreeTheftInsuranceQuotationForm.getFieldValue(el)) {
        const { id, activityKey } = getCoverageAndActivityKeyByID( el );

        const planSelected = stepThreeTheftInsuranceQuotationForm.getFieldValue(`plan-tab-${tabKey}`);

        const allCoveragesByTab = allCoveragesPerPlan.find( el => String(el.plan as string) === String(planSelected) )?.coverages;

        const coverage = allCoveragesByTab?.find( el => el.id === id);

        const sumaAseguradaInputName = generateInputID( id, activityKey );

        const primaInputName = generateInputID( id, activityKey, 'prima' );

        quotationCoverages.push({
          cobertura: coverage?.codigo!,
          riesgo: coverage?.codigoRiesgo!,
          sumaAsegurada: stepThreeTheftInsuranceQuotationForm.getFieldValue( sumaAseguradaInputName ),
          prima: stepThreeTheftInsuranceQuotationForm.getFieldValue( primaInputName ) ?? '0',
          pormilaje: '0'
        });
      }
    });

    return quotationCoverages;
  }, [stepThreeTheftInsuranceQuotationForm, allCoveragesPerPlan]);

  const generateTheftInsuranceComponentsInfo = useCallback((): Array<TheftInsuranceQuotationComponent> => {
    let components: Array<TheftInsuranceQuotationComponent> = [];

    tabs.forEach(({ key }: any) => {

      const location: string = stepTwoForm?.getFieldValue(`location-tab-${key}`);
      const provinceCode: string = stepTwoForm?.getFieldValue(`province-tab-${key}`); 
      
      const localitiesOfProvince = allLocalitiesPerProvince.find( el => el.provinceCode === provinceCode )?.localities;

      const riskZone = localitiesOfProvince?.find( el => `${el.sufijoCodigoPostal}${el.codigoPostal}` === location)?.zonaRiesgosVarios ?? '';

      const component: TheftInsuranceQuotationComponent = {
        nroComponente: key,
        codPlan: stepTwoForm?.getFieldValue(`plan-tab-${key}`) as number,
        tipoVivienda: 0, //* es 0 para polizas nuevas.
        caracteristicasBien: [], //* es vacío para Robo
        cantCaracteristicas: 0,
        sufijoCodigoPostal: location[0],
        codigoPostal: location.slice(1),
        zonaRiesgo: riskZone,
        buenResultado: 0, //* es 0 para polizas nuevas.
        cobertura: generateCoveragesInfo( key ),
        psua: 0, //* es 0 para polizas nuevas.
        capTarifa: '0',
        capTarifaInc1: '',
        capTarifaInc2: '',
        clasificacionRiesgo: 'A', 
      };

      components.push( component );
    });

    setTheftInsuranceQuotationComponents( components );

    return components;
  }, [ 
    tabs, 
    stepTwoForm, 
    allLocalitiesPerProvince, 
    generateCoveragesInfo, 
    setTheftInsuranceQuotationComponents 
  ]);

  const generateTheftInsuranceQuotationForm = useCallback((): TheftInsuranceQuotationRequest => {
    const components = generateTheftInsuranceComponentsInfo();
    const paymentMethodCode = theftInsurancePaymentMethodsForm.getFieldInstance('paymentMethod') ? theftInsurancePaymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1] : getValueDefaultPaymentMethod(paymentMethodsList)
    const administrativeSurcharge = theftInsurancePaymentMethodsForm.getFieldInstance('extraVariable') ? theftInsurancePaymentMethodsForm?.getFieldValue('extraVariable') : commertialConditions?.porcentajeExtraPrimaVariable

    return {
      token: quotationInfo?.token!,
      arse: quotationInfo?.arse!,
      rama: quotationInfo?.branch!,
      codigoTipoFormaPago: paymentMethodCode,
      cantComponentes: components.length,
      cliente: clientSelected!,
      componentes: components,
      recargoAdministrativo: administrativeSurcharge,
    }
  }, [
    quotationInfo, 
    clientSelected, 
    generateTheftInsuranceComponentsInfo, 
    theftInsurancePaymentMethodsForm, 
    commertialConditions
  ]);

  const handlerOnClickEmit = useCallback(async () => {
    setCurrentStep( QuotationStep.FOUR )
  }, [setCurrentStep]);

  const createTabs = useCallback(() => {
    const newTabs = tabs?.map(({ key }: any) => ({
      children: <TheftInsuranceQuotationFormByTab tabKey={key} />,
      forceRender: true,
      closable: false,
      key,
      label: `Robo ${key}`,
    }));

    setQuotationTabs(newTabs);

    setPlansSelectedByTabs(tabs, stepTwoForm);
  }, 
  [ 
    setQuotationTabs, 
    tabs, 
    stepTwoForm,
    setPlansSelectedByTabs,
  ]); 

  const updateFieldsBeforeQuotation = useCallback(({ componentes }: WebTheftInsuranceQuotation ) => {
    const paymentMethodCode = theftInsurancePaymentMethodsForm.getFieldValue('paymentMethod');
    const defaultPaymentMethod = paymentMethodsList?.find( el => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE );

    if( paymentMethodCode && paymentMethodsList?.find( el => `${el.codigoFormaDePago}-${el.nroPlanDePago}` === paymentMethodCode ) ) {
      theftInsurancePaymentMethodsForm.setFieldValue('paymentMethod', paymentMethodCode);
    } else {
      theftInsurancePaymentMethodsForm.setFieldValue('paymentMethod', `${defaultPaymentMethod?.codigoFormaDePago}-${defaultPaymentMethod?.nroPlanDePago}`);
    }
    
    if( !theftInsurancePaymentMethodsForm.getFieldValue('commission') && !theftInsurancePaymentMethodsForm.getFieldValue('extraVariable') ) {
      theftInsurancePaymentMethodsForm.setFieldValue('commission', Number( commertialConditions?.porcentajeDeComision ))
      theftInsurancePaymentMethodsForm.setFieldValue('extraVariable',  Number( commertialConditions?.porcentajeExtraPrimaVariable ))
    };

    componentes.forEach(({ nroDeComponente, coberturas }) => {
      const planSelected = stepThreeTheftInsuranceQuotationForm.getFieldValue(`plan-tab-${nroDeComponente}`);
      coberturas.forEach(({ codigoCobertura, codigoRiesgo, prima }) => {
        const primaInputName = generateInputID(`${codigoCobertura}-${codigoRiesgo}`, `theft-insurance-tab-${ nroDeComponente }-${planSelected}`, 'prima');
        stepThreeTheftInsuranceQuotationForm.setFieldValue( primaInputName, Number(prima) );
      })
    })

  }, [
    theftInsurancePaymentMethodsForm, 
    paymentMethodsList, 
    commertialConditions, 
    stepThreeTheftInsuranceQuotationForm
  ])

  const handlerOnClickQuotation = useCallback(async ( form: TheftInsuranceQuotationRequest ) => {
    setCanShowSpinner(true);

    try {
      await stepThreeTheftInsuranceQuotationForm.validateFields();

      const response = await QuotationService.postGenerateTheftInsuranceQuotation(form);
      
      if (response.data) {
        scrollToEmitButton();
        setLabelButton(QUOTATION_BUTTON.EMITIR);
        const tabsUserCanNotDelete: Array<number> = [];

        //recover ID from original coverate and set it into response
        response.data.cotizacionWebRobo.componentes.forEach(({ nroDeComponente, coberturas }) => {
          const planSelected = stepThreeTheftInsuranceQuotationForm.getFieldValue(`plan-tab-${nroDeComponente}`);

          const coveragesPerPlan = allCoveragesPerPlan.find(el => (el.plan as string) === String(planSelected))?.coverages ?? [];

          coberturas?.forEach(coverage => {
            const originalCoverage = coveragesPerPlan.find( (item) => item.codigo  === coverage.codigoCobertura && item.codigoRiesgo === coverage.codigoRiesgo );
            coverage.id = originalCoverage?.id;
            coverage.subCategoriaDeTextos = originalCoverage?.subCategoriaDeTextos;
          });
          tabsUserCanNotDelete.push(Number(nroDeComponente));
        });
        
        setTabsUserCanNotDelete(tabsUserCanNotDelete);
        setQuotationResponse(response.data.cotizacionWebRobo);
        updateFieldsBeforeQuotation( response.data.cotizacionWebRobo );
        return;
      };
    } catch (error: any) {
      if (error && error?.errorFields?.length > 0) {
        handleTabErrors(error?.errorFields, ['tab'], 'Robo');
      }
    } finally {
      setCanShowSpinner(false)
    }
  }, [
    stepThreeTheftInsuranceQuotationForm, 
    setLabelButton,
    setCanShowSpinner, 
    setQuotationResponse, 
    updateFieldsBeforeQuotation,
    setTabsUserCanNotDelete
  ]);

  const handlerOnClick = useCallback(async () => {
    try {
      const form: TheftInsuranceQuotationRequest = generateTheftInsuranceQuotationForm();
      if (labelButton === QUOTATION_BUTTON.EMITIR) return await handlerOnClickEmit();
  
      if (labelButton === QUOTATION_BUTTON.COTIZAR || labelButton === QUOTATION_BUTTON.RECOTIZAR)
        return await handlerOnClickQuotation(form);
    } catch (error) {}
  }, [ 
    labelButton, 
    generateTheftInsuranceQuotationForm, 
    handlerOnClickEmit, 
    handlerOnClickQuotation
  ]);

  const fillStepThreeServices = useCallback(async () => {
    try {
      createTabs();
      await getCommertialsConditions();
    } catch (error) {}
  }, [
    getCommertialsConditions,
    createTabs,
  ]);
  
  const fillStepThreeData = useCallback(async (isRenewal: boolean) => {
    let quotationToProccess: LoadQuotationToRenewResponse;
    if (isRenewal) {
      quotationToProccess = quotationToRenewal;
    } else {
      quotationToProccess = savedQuotation;
    }
    //* Se setea vacio ya que no tenemos la primera respuesta de la COTIZACIÓN en un retome. 
    setQuotationResponse({
      sumaAsegurada: "",
      premio: "",
      prima: "",
      componentes: quotationToProccess.bienes.map((bien: any) => {

        return {
          nroDeComponente: bien.numeroComponente,
          producto: "",
          tipoVivienda: "",
          codigoPostal: "",
          sufijoCodigoPostal: "",
          zonaRiesgo: "",
          buenResultado: "",
          sumaAsegurada: "",
          premio: "",
          requiereInspeccion: "",
          porcentajeSumaAsegurada: "",
          capituloTarifa: "",
          capituloTarifaInc1: "",
          agravamiento: "",
          caracteristicas: isRenewal ? [] : savedQuotation.caracteristicasSeleccionadas,
          coberturas: isRenewal ? bien.coberturasSeleccionadas : savedQuotation.coberturasSeleccionadas,
          impuestos: [
            {
              codigoCobertura: "",
              impComision: "",
              impExtraPrimaFija: "",
              impImpuestosInternos: "",
              impIva: "",
              impIvaPercepcion: "",
              impIvaRnl: "",
              impIibbPercepcion: "",
              impIibbRiesgo: "",
              impIibbEmpresa: "",
              porImpuestosInternos: "",
              porIva: "",
              porIvaRnl: "",
              porIvaPercepcion: "",
              porTasaSsn: "",
              porServiciosSociales: "",
              impExtraPrimaVar: "",
              impRecargoFinanciero: "",
              selladoDeEmpresa: "",
              selladoDelRiesgo: "",
              impServiciosSociales: "",
              impTasaSsn: "",
              porComision: "",
              porExtraPrimaVar: "",
              porRecargoFinanciero: "",
              calculoImpuestos: ""
            }
          ],
          bonificaciones: []
        }
      }),
      impuestos: {
        rama: "1",
        cantPolizaXRama: "",
        prima: "",
        porRecargoFinanciero: "",
        impRecargoFinanciero: "",
        impExtraPrimaFija: "",
        impExtraPrimaVar: "",
        impSubtotalPrima: "",
        selladoDelRiesgo: "",
        selladoDeEmpresa: "",
        porImpuestosInternos: "",
        impImpuestosInternos: "",
        porServiciosSociales: "",
        impServiciosSociales: "",
        porTasaSsn: "",
        impTasaSsn: "",
        impIibbPercepcion: "",
        impIibbEmpresa: "",
        impIibbRiesgo: "",
        impIvaRnl: "",
        porIvaRnl: "",
        impIva: "",
        porIva: ""
      },
      condicionesComerciales: {
        cantPolizaXRama: "",
        porRecargoAdminist: "",
        impRecargoAdminist: "",
        porComisionProd: "",
        impComision: "",
        porRecargoAdminist1: "",
        porComisionProd1: ""
      },
      condicionesComerciales1: {
        rama: "1",
        porcRecargoAdminist: ""
      }
    });

    quotationToProccess.bienes.forEach((bien: any, index:number) => {
      const _tab = index + 1;

      bien.coberturasSeleccionadas.forEach((cobertura: any) => {
        stepThreeTheftInsuranceQuotationForm.setFields([
          { name: `checkbox_${cobertura.codigo}-${cobertura.codigoRiesgo}_theft-insurance-tab-${_tab}-${bien.planSeleccionado}`, value: true },
          { name: `prima_${cobertura.codigo}-${cobertura.codigoRiesgo}_theft-insurance-tab-${_tab}-${bien.planSeleccionado}`, value: cobertura.prima},
          { name: `sumaAsegurada_${cobertura.codigo}-${cobertura.codigoRiesgo}_theft-insurance-tab-${_tab}-${bien.planSeleccionado}`, value: cobertura.sumaAsegurada },
        ]);
      });
    });

    theftInsurancePaymentMethodsForm.setFields([
      { name: `paymentMethod`, value: `${quotationToProccess.formaDePago.codigo}-${quotationToProccess.formaDePago.codigoDePlanDePagos}` },
      { name: `commission`, value: quotationToProccess.formaDePago.porcentajeComision },
      { name: `extraVariable`, value: quotationToProccess.formaDePago.porcentajeExtraPrimaVariable },
      { name: `variation`, value: Number(quotationToProccess.formaDePago.porcentajeComision) - commertialConditions!.porcentajeDeComision }
    ]);

    setLabelButton(QUOTATION_BUTTON.RECOTIZAR);
  }, [
    savedQuotation,
    quotationToRenewal, 
    commertialConditions, 
    setQuotationResponse, 
    stepThreeTheftInsuranceQuotationForm, 
    theftInsurancePaymentMethodsForm, 
    setLabelButton,
  ]);

  useEffect(() => {
    if (quotationResponse) {
      setIsPlanTypeADisabled(true);
      setIsPlanTypeCDisabled(true);
    }
  }, [quotationResponse]);

  useEffect(() => {
    if (removedTabIndexes.length && currentStep === QuotationStep.THREE && formValues) {
      updateFormsAfterRemoveTabs(removedTabIndexes, [stepThreeTheftInsuranceQuotationForm], formValues);
      setRemovedTabIndexes([]);
    };
  }, [removedTabIndexes, currentStep, formValues]);

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && !loadSaveQuotationState) {
      getCommertialsConditions();
    }
  }, [currentStep, loadSaveQuotationState, getCommertialsConditions]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING);
      fillStepThreeServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING &&
      commertialConditions
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED);
    }
  }, [
    loadSaveQuotationState,
    commertialConditions,
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING);
      fillStepThreeData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING &&
      quotationTabs.length === tabs.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      allCoveragesPerPlan.length > 0
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED);
    }
  }, [
    loadSaveQuotationState,
    quotationTabs,
    tabs,
    labelButton,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING) {
      handlerOnClick();
    }
  }, [
    loadSaveQuotationState
  ]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && !renewalQuotationState) {
      getCommertialsConditions();
    }
  }, [currentStep, renewalQuotationState, getCommertialsConditions]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL);
      fillStepThreeServices();
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL &&
      commertialConditions
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    commertialConditions,
  ]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL);
      fillStepThreeData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL &&
      quotationTabs.length === tabs.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      allCoveragesPerPlan.length > 0
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    quotationTabs,
    tabs,
    labelButton,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL) {
      handlerOnClick();
    }
  }, [
    renewalQuotationState
  ]);

  const values = useMemo(
    () => ({
      stepThreeTheftInsuranceQuotationForm,
      theftInsurancePaymentMethodsForm,
      labelButton,
      setLabelButton,
      quotationResponse,
      setQuotationResponse,
      commertialConditions,
      clearPrimaFieldsOnFormChange,
      generateTheftInsuranceQuotationForm,
      theftInsuranceQuotationComponents,
      handlerOnClick,
      createTabs,
      quotationTabs,
      setQuotationTabs,
      activeTab,
      setActiveTab,
    }),
    [
      stepThreeTheftInsuranceQuotationForm,
      theftInsurancePaymentMethodsForm,
      labelButton,
      setLabelButton,
      quotationResponse,
      setQuotationResponse,
      commertialConditions,
      clearPrimaFieldsOnFormChange,
      generateTheftInsuranceQuotationForm,
      theftInsuranceQuotationComponents,
      handlerOnClick,
      createTabs,
      quotationTabs,
      setQuotationTabs,
      activeTab,
      setActiveTab,
    ]
  );

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  );
};